import type { HiiveActivityCommand } from "@/hooks/use-live-activity-commands";
import { withActivityCommand } from "./activity-command-context";

type CommandResult = unknown;
export type ActivityCommandHandlers = Partial<{
  onMovementStart: (activityType: string, atMs: number) => CommandResult;
  onMovementPause: () => CommandResult;
  onMovementResume: () => CommandResult;
  onMovementEnd: () => CommandResult;
  onWorkoutSkipRest: () => CommandResult;
  onWorkoutCompleteSet: (setId?: string) => CommandResult;
  onWorkoutAddRest: (seconds: number) => CommandResult;
  onWorkoutSubtractRest: (seconds: number) => CommandResult;
  onWorkoutEnd: () => CommandResult;
}>;

export type CommandScope = {
  accountId?: string;
  sessionId?: string;
  hasAppliedCommand?: (id: string) => boolean;
};
export type Subscriber = {
  handlers: ActivityCommandHandlers;
  scope: CommandScope;
};
function handlerFor(
  command: HiiveActivityCommand,
  handlers: ActivityCommandHandlers,
): (() => CommandResult) | null {
  const seconds = Math.max(0, Math.min(3600, command.value ?? 15));
  switch (`${command.activity}:${command.action}`) {
    case "movement:start":
      return handlers.onMovementStart
        ? () =>
            handlers.onMovementStart?.(
              command.activityType ?? "run",
              command.at,
            )
        : null;
    case "movement:pause":
      return handlers.onMovementPause ?? null;
    case "movement:resume":
      return handlers.onMovementResume ?? null;
    case "movement:end":
      return handlers.onMovementEnd ?? null;
    case "workout:skipRest":
      return handlers.onWorkoutSkipRest ?? null;
    case "workout:completeSet":
      return handlers.onWorkoutCompleteSet
        ? () => handlers.onWorkoutCompleteSet?.(command.setId)
        : null;
    case "workout:addRest":
      return handlers.onWorkoutAddRest
        ? () => handlers.onWorkoutAddRest?.(seconds)
        : null;
    case "workout:subtractRest":
      return handlers.onWorkoutSubtractRest
        ? () => handlers.onWorkoutSubtractRest?.(seconds)
        : null;
    case "workout:end":
      return handlers.onWorkoutEnd ?? null;
    default:
      return null;
  }
}

export async function dispatchActivityCommand(
  command: HiiveActivityCommand,
  subscribers: Iterable<{ current: Subscriber }>,
  appliedInMemory: Set<string>,
): Promise<boolean> {
  if (
    !Number.isFinite(command.at) ||
    command.at <= 0 ||
    command.at > Date.now() + 60_000
  )
    return false;
  if (command.value !== undefined && !Number.isFinite(command.value))
    return false;
  if (command.id && appliedInMemory.has(command.id)) return true;
  for (const subscriber of subscribers) {
    const { handlers, scope } = subscriber.current;
    const handler = handlerFor(command, handlers);
    if (!handler || !scope.accountId) continue;
    if (command.accountId && command.accountId !== scope.accountId) continue;
    // Start only adopts an already-running, owner-checked native recording.
    // Legacy native commands lack accountId; they never initiate capture here.
    if (
      command.action !== "start" &&
      (!command.sessionId || command.sessionId !== scope.sessionId)
    )
      continue;
    if (command.action === "completeSet" && !command.setId) return false;
    if (command.id && scope.hasAppliedCommand?.(command.id)) return true;
    const outcome = await withActivityCommand(command.id, handler, command.at);
    if (
      outcome &&
      typeof outcome === "object" &&
      "ok" in outcome &&
      !outcome.ok
    )
      return false;
    if (command.id) appliedInMemory.add(command.id);
    return true;
  }
  return false;
}
